import { Injectable } from '@angular/core';
import { API } from './api';
import { ClassProvider } from './class';
import { BaseProvider } from './base';
import { BRAIN_ACCURACY_DESCRIPTORS } from '../components/workspace/accuracy/constants';
import { DownloadProvider } from './download';
import { DatasetType } from './dataset';
import { DetectionPrediction } from './inference';
import { ClassThreshold } from '../components/evaluate-brain/detection-thresholds/detection-thresholds.component';
import { JobType } from './job';
import { TrackableJobProvider } from './trackable-job';
import { TranslateService } from '@ngx-translate/core';

export class BrainHistory {
    accuracy: number;
    numPolygons: number;
}

export class BrainDetails {
    accuracy: number;
    averagePrecision: number; // averagePrecision and averageRecall are detection specific metrics.
    averageRecall: number;
    brainVersionIdentifier: string;
    classes: BrainEnvClass[];
    dateTrained: string;
    dateReleased: string;
    dirty: boolean;
    isFirstBuild: boolean;
    explainable: boolean;
    miapCOCO: number;
    miapVOC: number;
    numFrames: number;
    numTrainedPolygons: number;
    numValidationPolygons: number;
    precision: number;
    recall: number;
    rois: Array<{ data: number[][] }>;
    normalThreshold: number;
    originalNormalThreshold: number;
    brainVersionType: BrainVersionType;
    confidenceThreshold?: number;
    iouThreshold?: number;
    useClassConfidenceThresholds?: boolean;
    hiFiPixelThreshold?: number | null;
    originalHiFiPixelThreshold?: number | null;
    hiFiOverlayOpacity?: number | null;
    hiFiOverlayColor?: string;
}

// Overwrites the possibly undefined properties in the base interface
export class DetectionBrainEnvironmentStats extends BrainDetails {
    confidenceThreshold: number;
    iouThreshold: number;
    useClassConfidenceThresholds: boolean;
}

export enum BrainEnvironmentName {
    Current = 'current',
    CurrentFrozen = 'currentFrozen',
    Staging = 'staging',
    Production = 'production',
    AnomalyHiFi = 'anomalyHiFi'
}

export class BrainEnvironments {
    current: BrainDetails;
    currentFrozen?: BrainDetails;
    currentAnomalyHiFi?: BrainDetails;
    staging: BrainDetails;
    production: BrainDetails;
}

export class Brain {
    accuracy: number;
    brainIdentifier: string;
    dateAdded: string;
    dateUpdated: string;
    name: string;
    classStats: Object[];
    history: BrainHistory[];
    trained: boolean;
    numPolygons: number;
    requiredPolygons: number;
    numImages: number;
    ready: boolean;

    public getFormattedAccuracy() {
        return `${Math.round(this.accuracy * 100)}%`;
    }
}

export class BrainClass {
    name: string;
    color: string;
    numPolygons: number;
    accuracy: number;
    ready: boolean;
}

export class BrainEnvClass {
    classIdentifier: string;
    numTrainedPolygons: number;
    numValidationPolygons: number;
    accuracy: number;
    precision: number;
    recall: number;
    color: string;
    name: string;
    confidenceThreshold?: number;
}

export enum BrainVersionType {
    Learning = 'Learning',
    Frozen = 'Frozen',
    Custom = 'Custom',
    AnomalyHiFi = 'AnomalyHiFi'
}

export interface ConfidenceThresholdClassMap {
    [key: string]: number;
}

export interface ConfidenceThresholdSpec {
    overall?: number;
    classes?: ConfidenceThresholdClassMap;
}

export interface DetectionThresholdsSpec {
    confidenceThresholdSpec: ConfidenceThresholdSpec;
    iouThreshold: number;
}

export interface DetectionClassMetrics {
    precision: number;
    recall: number;
}

export interface DetectionClassMetricsMap {
    [key: string]: DetectionClassMetrics;
}

export interface DetectionMetrics {
    averagePrecision: number;
    averageRecall: number;
    miapVOC: number;
    miapCOCO: number;
    classMetrics: DetectionClassMetricsMap;
}

export interface BrainVersionDisplayInfo {
    label: string;
    icon: string;
}

export const BrainVersionDisplayInfoMap = {
    'Anomaly Recognition': {
        Learning: { label: 'Anomaly Recognizer', icon: 'tonality' },
        AnomalyHiFi: { label: 'Anomaly Hi-Fi', icon: 'lens_blur' }
    },
    'Scene Recognition': {
        Learning: { label: 'Learning Classifier', icon: 'school' },
        Frozen: { label: 'Static Classifier', icon: 'auto_graph' }
    },
    Detection: {
        Learning: { label: 'Detector', icon: 'fit_screen' },
        Custom: { label: 'Detector', icon: 'fit_screen' }
    }
};

export enum BrainExportType {
    BrainBuilder = 'brainbuilder',
    Firefly = 'firefly',
    Sony = 'sony'
}

@Injectable()
export class BrainProvider extends BaseProvider {
    private brains: Brain[] = [];
    private training = false;
    private predicting = false;

    public readonly DIRTY_WARNING =
        'Brain is out-of-sync with dataset classes or tagged images.  Retraining your brain is highly recommended.';

    constructor(
        private api: API,
        private classProvider: ClassProvider,
        private downloadProvider: DownloadProvider,
        private trackableJobProvider: TrackableJobProvider,
        private translate: TranslateService
    ) {
        super();
    }

    /**
     * Gets n brains
     * @param limit: The number of brains you want
     * Leave this blank to get all brains
     */
    public getBrains(limit: number = this.brains.length): Brain[] {
        return this.brains.slice(0, limit);
    }

    /**
     * Fetches the latest brain listings from BrainBank
     */
    protected async fetchLatestBrains(): Promise<Brain[]> {
        try {
            await this.ensureDependenciesLoaded([this.classProvider]);

            const latestBrains = await this.api.get('brains');

            // calculate the total # of polygons in a brain
            this.brains = latestBrains.message.brains.map(obj => {
                const brain = Object.assign(new Brain(), obj);
                const numClasses = Object.keys(brain.classStats).length;

                // Let's calculate this once so everything can use it as a toggle
                brain.ready = brain.numPolygons >= numClasses * this.classProvider.THRESHOLD;
                brain.requiredPolygons = numClasses * this.classProvider.THRESHOLD;

                return brain;
            });

            return this.brains;
        } catch (e) {
            throw e;
        }
    }

    public async load(force: boolean = false) {
        return this.ensureLoaded(() => this.fetchLatestBrains(), force);
    }

    /**
     * Removes brain information for the user
     */
    public resetInfo(): void {
        this.brains = [];
        super.resetInfo();
    }

    /**
     * Creates a new brain and adds it to local brains
     * @param name: The name of new brain
     * @return returns the new created brain
     */
    public async createBrain(name: string): Promise<Brain> {
        try {
            const brainResp = await this.api.post('brains', { name });

            const brain = brainResp.message;
            brain.numPolygons = 0;

            // add brain to the existing brain list
            this.brains.push(brain);

            return brain;
        } catch (e) {
            throw e;
        }
    }

    /**
     * Gets active brains name
     * @return {Brain} name of active brain(first for now)
     */
    public getActiveBrain(): Brain | null {
        return this.brains !== null && this.brains.length > 0 ? this.brains[0] : null;
    }

    public hasActiveBrain(): boolean {
        return this.brains.length > 0;
    }

    /**
     * Merge the user's classes with the active brain to create a
     * merged object that has properties of both
     *
     * @param {Brain} brain
     * @param {boolean} sorted
     */
    public getBrainClasses(brain: Brain, sorted?: boolean): BrainClass[] {
        // First find only classes that exist for this brain
        // Then combine them into a single BrainClass object
        const brainClasses = this.classProvider
            .getClasses()
            .filter(classObj => brain.classStats[classObj.id])
            .map(classObj => {
                const brainClass = new BrainClass();
                brainClass.name = classObj.name;
                brainClass.color = classObj.color;
                brainClass.numPolygons = brain.classStats[classObj.id].numPolygons;
                brainClass.accuracy = brain.classStats[classObj.id].accuracy;
                brainClass.ready = brainClass.numPolygons >= this.classProvider.THRESHOLD;

                return brainClass;
            });

        if (sorted) {
            brainClasses.sort((a: BrainClass, b: BrainClass) => {
                if (a.numPolygons < b.numPolygons) {
                    return 1;
                }

                if (a.numPolygons > b.numPolygons) {
                    return -1;
                }

                return 0;
            });
        }

        return brainClasses;
    }

    /**
     * Return the number of classes associated with the brain that include trained images
     */
    public getNumTrainedClasses(brainDetails: BrainDetails): number {
        let numTrainedClasses = 0;
        for (const brainClass of brainDetails.classes) {
            if (brainClass.numTrainedPolygons > 0) {
                numTrainedClasses++;
            }
        }

        return numTrainedClasses;
    }

    public hasBrain(): boolean {
        return this.brains.length > 0;
    }

    /**
     * Keeps track of whether the brain is currently training or not
     */
    public isTraining(): boolean {
        return this.training;
    }

    /**
     * Keeps track of whether the brain is currently training or not
     */
    public isPredicting(): boolean {
        return this.predicting;
    }

    /**
     * Train the brain
     * @param {Brain} brain
     * @param {string} imageID
     */
    public async train(brain: Brain, imageID: string): Promise<any> {
        try {
            this.training = true;

            await this.api.post(`brains/${brain.brainIdentifier}/train`, {
                imageIdentifiers: [imageID]
            });

            this.training = false;
        } catch (err) {
            this.training = false;
            throw err;
        }
    }

    public async predict(brain: Brain, imageID: string): Promise<any> {
        try {
            this.predicting = true;

            const result = await this.api.post(`brains/${brain.brainIdentifier}/predict`, {
                imageIdentifiers: [imageID]
            });

            this.predicting = false;

            return result.message;
        } catch (err) {
            this.predicting = false;
            throw err;
        }
    }

    public formatAccuracy(accuracy: number): string {
        return `${Math.round(accuracy * 100)}%`;
    }

    public async getBrainEnvironments(id: string, denormalizeROI: boolean = false): Promise<BrainEnvironments> {
        try {
            const brainEnvs = await this.api.get(
                `brains/${id}/environments?&denormalizeROI=${denormalizeROI}&environment[]=current&environment[]=staging&environment[]=production`
            );
            return brainEnvs.message;
        } catch (err) {
            throw err;
        }
    }

    public async getSingleBrainEnvironment(id: string, environment: string): Promise<BrainEnvironments> {
        try {
            const brainEnvs = await this.api.get(`brains/${id}/environments?environment=${environment}`);
            return brainEnvs.message;
        } catch (err) {
            throw err;
        }
    }

    public async getCurrentBrainDetails(
        id: string,
        brainVersionType: BrainVersionType = BrainVersionType.Learning,
        denormalizeROI: boolean = false
    ): Promise<BrainDetails> {
        try {
            const brainEnvs = await this.debounce(
                `${id}/environments?brainVersionType=${brainVersionType}&denormalizeROI=${denormalizeROI}`,
                () => {
                    return this.api.get(
                        `brains/${id}/environments?brainVersionType=${brainVersionType}&environment[]=current&denormalizeROI=${denormalizeROI}`
                    );
                },
                250
            );

            return brainEnvs.message.current;
        } catch (err) {
            throw err;
        }
    }

    public extractROIDataFromBrain(brainDetails: BrainDetails): number[][] | null {
        return brainDetails && brainDetails.rois && brainDetails.rois[0] && brainDetails.rois[0].data
            ? brainDetails.rois[0].data
            : null;
    }

    public async promote(
        to: string,
        from: string,
        id: string,
        brainVersionType = BrainVersionType.Learning
    ): Promise<{ success: true; message: { releaseIdentifier: string } }> {
        try {
            let url = `brains/${id}/promote`;
            if (brainVersionType && from == BrainEnvironmentName.Current) {
                url += `?brainVersionType=${brainVersionType}`;
            }

            const response = await this.api.post(url, {
                to,
                from
            });
            return response;
        } catch (err) {
            throw err;
        }
    }

    public async delete(id: string, env: string) {
        try {
            const response = await this.api.delete(`brains/${id}/environments/${env}/releases`);
            return response;
        } catch (err) {
            throw err;
        }
    }

    private getExportPathEnd(brainExportType: BrainExportType): string {
        let exportPathEnd = '';
        if (brainExportType == BrainExportType.Firefly) {
            exportPathEnd = '/firefly';
        } else if (brainExportType == BrainExportType.Sony) {
            exportPathEnd = '/sony';
        }

        return exportPathEnd;
    }

    public async download(brainVersionIdentifier: string, brainExportType: BrainExportType): Promise<void> {
        const pathEnd = this.getExportPathEnd(brainExportType);
        const response = await this.api.post(`brain-versions/${brainVersionIdentifier}/exports${pathEnd}`);

        if (response.success && response.message.signedUrl) {
            console.log(`Brain download signed URL: ${response.message.signedUrl}`);

            await this.downloadProvider.download(response.message.signedUrl);
        }
    }

    public async kickOffDownloadJob(
        datasetIdentifier: string,
        brainVersionIdentifier: string,
        brainExportType: BrainExportType
    ): Promise<void> {
        const pathEnd = this.getExportPathEnd(brainExportType);

        try {
            await this.api.post(`brain-versions/${brainVersionIdentifier}/exports${pathEnd}`);
            await this.trackableJobProvider.restartJobPolling();
        } catch (error) {
            this.trackableJobProvider.notifyJobRequestFailed(datasetIdentifier, JobType.BrainExport);

            throw error;
        }
    }

    public async downloadExportedBrain(jobIdentifier: string, brainVersionIdentifier: string): Promise<void> {
        const jobResult = await this.api.get(
            `brain-versions/${brainVersionIdentifier}/exported-brain/${jobIdentifier}`
        );

        if (jobResult.success && jobResult.signedUrl) {
            console.log(`Brain download signed URL: ${jobResult.signedUrl}`);

            await this.downloadProvider.download(jobResult.signedUrl);
            await this.trackableJobProvider.dismiss(jobIdentifier);
        }
    }

    private checkMoreClassesThan(classes: BrainEnvClass[], target: number): boolean {
        for (const brainEnvClass of classes) {
            const currentClass: BrainEnvClass = brainEnvClass;
            const numClassImages: number = currentClass.numTrainedPolygons + currentClass.numValidationPolygons;
            if (numClassImages < target) {
                return false;
            }
        }
        return true;
    }

    private checkFewerClassesThan(classes: BrainEnvClass[], target: number): boolean {
        if (!classes || classes.length == 0) {
            return true;
        }

        for (const brainEnvClass of classes) {
            const currentClass: BrainEnvClass = brainEnvClass;
            const numClassImages: number = currentClass.numTrainedPolygons + currentClass.numValidationPolygons;
            if (numClassImages < target) {
                return true;
            }
        }

        return false;
    }

    private checkClassAccuraciesGreaterThan(classes: BrainEnvClass[], target: number): boolean {
        for (let classI = 0; classI < classes.length; classI++) {
            if (classes[classI].accuracy >= target) {
                return true;
            }
        }
        return false;
    }

    private checkClassAccuraciesLessThan(classes: BrainEnvClass[], target: number): boolean {
        for (let classI = 0; classI < classes.length; classI++) {
            if (classes[classI].accuracy < target) {
                return true;
            }
        }
        return false;
    }

    private isBrainScoreStillLearning(brainDetails: BrainDetails): boolean {
        return this.checkFewerClassesThan(brainDetails.classes, 10);
    }

    private isBrainScoreLow(brainDetails: BrainDetails): boolean {
        if (this.checkFewerClassesThan(brainDetails.classes, 20)) {
            return true;
        } else if (brainDetails.accuracy < 0.8) {
            return true;
        } else if (this.checkClassAccuraciesLessThan(brainDetails.classes, 0.7)) {
            return true;
        }

        return false;
    }

    private isBrainScoreGood(brainDetails: BrainDetails): boolean {
        if (brainDetails.accuracy < 0.9) {
            return true;
        } else if (this.checkClassAccuraciesLessThan(brainDetails.classes, 0.9)) {
            return true;
        } else if (this.checkFewerClassesThan(brainDetails.classes, 50)) {
            return true;
        }

        return false;
    }

    private isBrainScoreGreat(brainDetails: BrainDetails): boolean {
        if (!(brainDetails.accuracy >= 0.9)) {
            return false;
        } else if (!this.checkClassAccuraciesGreaterThan(brainDetails.classes, 0.9)) {
            return false;
        } else if (!this.checkMoreClassesThan(brainDetails.classes, 0.5)) {
            return false;
        }

        return true;
    }

    public getBrainScore(brainDetails: BrainDetails): string {
        if (this.isBrainScoreStillLearning(brainDetails)) {
            return BRAIN_ACCURACY_DESCRIPTORS.STILL_LEARNING;
        } else if (this.isBrainScoreLow(brainDetails)) {
            return BRAIN_ACCURACY_DESCRIPTORS.LOW;
        } else if (this.isBrainScoreGood(brainDetails)) {
            return BRAIN_ACCURACY_DESCRIPTORS.GOOD;
        } else if (this.isBrainScoreGreat(brainDetails)) {
            return BRAIN_ACCURACY_DESCRIPTORS.GREAT;
        } else {
            return this.translate.instant('Brain class check error');
        }
    }

    public getBrainVersionDisplayInfo(
        datasetType: DatasetType,
        brainVersionType: BrainVersionType
    ): BrainVersionDisplayInfo {
        const datasetInfoMap = BrainVersionDisplayInfoMap[datasetType];
        if (!datasetInfoMap) {
            throw new Error(`Invalid dataset type: ${datasetType}`);
        }

        const brainVersionTypeInfo = datasetInfoMap[brainVersionType];
        if (!brainVersionTypeInfo) {
            throw new Error(`Invalid brain version type: ${brainVersionType} for dataset type ${datasetType}`);
        }

        return brainVersionTypeInfo;
    }

    public async getDetectionThresholdsMetrics(
        datasetIdentifier: string,
        detectionThresholdsSpec: DetectionThresholdsSpec
    ): Promise<DetectionMetrics> {
        const confidenceThresholdSpec = encodeURIComponent(
            JSON.stringify(detectionThresholdsSpec.confidenceThresholdSpec)
        );

        const response = await this.api.get(
            `datasets/${datasetIdentifier}/detection-thresholds/metrics?confidenceThresholdSpec=${confidenceThresholdSpec}&iouThreshold=${detectionThresholdsSpec.iouThreshold}`
        );

        const classMetrics: DetectionClassMetricsMap = {};
        for (const className in response.message.classMetrics) {
            const metrics = response.message.classMetrics[className];

            classMetrics[className] = {
                precision: metrics.precision,
                recall: metrics.recall
            };
        }

        return {
            averagePrecision: response.message.averagePrecision,
            averageRecall: response.message.averageRecall,
            miapVOC: response.message.miapVOC,
            miapCOCO: response.message.miapCOCO,
            classMetrics
        };
    }

    public async kickoffUpdateDetectionThresholdsJob(
        datasetIdentifier: string,
        detectionThresholdsSpec: DetectionThresholdsSpec
    ): Promise<string> {
        const result = await this.api.put(`datasets/${datasetIdentifier}/detection-thresholds`, {
            confidenceThresholdSpec: detectionThresholdsSpec.confidenceThresholdSpec,
            iouThreshold: detectionThresholdsSpec.iouThreshold,
            runAsJob: true
        });

        return result.jobIdentifier;
    }

    /**
     * Call the endpoint to persist hifi pixel parameters
     * This is run as an async job in the back-end so it will now return pretty much immediately with a jobIdentifier
     * It will be up to us to poll the job state
     */
    public async kickoffSaveHiFiPixelParametersJob(
        datasetIdentifier: string,
        pixelThreshold: number,
        overlayOpacity: number,
        overlayColor: string
    ): Promise<string> {
        const result = await this.api.post(`datasets/${datasetIdentifier}/hifi-pixel-parameters?runAsJob=true`, {
            pixelThreshold,
            overlayOpacity,
            overlayColor: overlayColor.replace('#', '')
        });

        return result.jobIdentifier;
    }

    public getFilteredDetectionPredictions(
        predictions: DetectionPrediction[],
        confidenceThresholdSpec: ConfidenceThresholdSpec
    ): DetectionPrediction[] {
        return predictions.filter(p => {
            return this.satisfiesConfidenceThreshold(confidenceThresholdSpec, p.classObject.name, p.confidence);
        });
    }

    public getConfidenceThreshold(confidenceThresholdSpec: ConfidenceThresholdSpec, className: string): number {
        if (confidenceThresholdSpec.classes !== undefined && confidenceThresholdSpec.classes[className]) {
            return confidenceThresholdSpec.classes[className];
        }

        // Fall back to overall if a class threshold isn't defined
        if (confidenceThresholdSpec.overall !== undefined) {
            return confidenceThresholdSpec.overall;
        }

        console.log(`Cannot find a valid confidence threshold spec for class ${className}`);
        return 0.5;
    }

    /**
     * Determines whether the provided confidence is high enough compared to what is required
     */
    public satisfiesConfidenceThreshold(
        confidenceThresholdSpec: ConfidenceThresholdSpec,
        className: string,
        confidence: number
    ): boolean {
        const confidenceThreshold = this.getConfidenceThreshold(confidenceThresholdSpec, className);
        return confidence >= confidenceThreshold;
    }

    public getDetectionThresholdsSpec(
        confidenceThreshold: number,
        editingPerClass: boolean,
        classThresholds: ClassThreshold[],
        iouThreshold: number
    ): DetectionThresholdsSpec {
        const confidenceThresholdSpec: ConfidenceThresholdSpec = {
            overall: confidenceThreshold
        };

        if (editingPerClass) {
            confidenceThresholdSpec.classes = {};
            for (const obj of classThresholds) {
                confidenceThresholdSpec.classes[obj.name] = obj.confidenceThreshold;
            }
        }

        return {
            iouThreshold,
            confidenceThresholdSpec
        };
    }
}



// WEBPACK FOOTER //
// ./src/providers/brain.ts